/** A string key used to group transitions by a overlap criteria depending on the type of transition */
export type OverlapKey = string;

export type OverlapItem =
	| {
			type: 'direct-transition';
			transitionId: string;
			isReversed: boolean;
	  }
	| {
			type: 'shared-transition';
			transitionId: string;
			isReversed: boolean;
			linkIndex: number;
	  };

export type OverlapPlacement = Omit<OverlapItem, 'isReversed'> & {
	/** Fractional value between 0 and 1 representing where a label should sit on a transition having taken into account the other labels */
	placement: number;
	overlapKey: OverlapKey;
};

export const HALFWAY_PLACEMENT = 0.5;

/** Concats to group items, inserts at front if `isReversed` is false */
export function addToOverlapsGroup(
	overlaps: Map<OverlapKey, OverlapItem[]>,
	overlapKey: OverlapKey,
	overlapItem: OverlapItem,
): void {
	const previousValue = overlaps.get(overlapKey) ?? [];

	if ('isReversed' in overlapItem && overlapItem.isReversed) {
		overlaps.set(overlapKey, [overlapItem, ...previousValue]);
	} else {
		overlaps.set(overlapKey, [...previousValue, overlapItem]);
	}
}

/** Generates an overlap key where the criteria for overlap is two ports */
export function overlapKeyByPorts(
	startPortKey: string,
	endPortKey: string,
): {
	overlapKey: OverlapKey;
	isReversed: boolean;
} {
	// start and end port key order is ignored to determine if route between two ports is overlapping
	// however we need to if its reversed or not, to do a cheap order-insensitive check for overlaps
	// we can simply use a string alphabetic comparison
	const isReversed = startPortKey > endPortKey;

	return {
		overlapKey: isReversed
			? keyWith('by-ports', endPortKey, startPortKey)
			: keyWith('by-ports', startPortKey, endPortKey),
		isReversed,
	};
}

/** Generate overlap key for overlapping calculations by concatenating string information */
export function keyWith(...strings: string[]): OverlapKey {
	return `overlap[${strings.join(';')}]`;
}

/** Helper function to resolve exact label placement given a transition and overlap placement results */
export function getLabelPlacement(
	transitionId: string,
	transitionLabelPlacements: OverlapPlacement[],
	/** Additionally filter on linkIndex for shared transitions */
	linkIndex?: number,
): number {
	const match = transitionLabelPlacements.find((x) => {
		if (x.transitionId !== transitionId) {
			return false;
		}

		if ('linkIndex' in x && x.linkIndex !== linkIndex) {
			return false;
		}

		return true;
	});

	return match?.placement ?? HALFWAY_PLACEMENT;
}
